






import Vue from 'vue';
import {
  defineComponent,
  onMounted,
  reactive,
  ref,
  Ref,
  UnwrapRef,
  toRefs,
  watch,
  getCurrentInstance,
  ComponentInternalInstance,
} from '@vue/composition-api';
interface TriggerElm {
  ele: HTMLElement | null;
  disabled: boolean;
}
const generateId = (): number => Math.floor(Math.random() * 10000);
const broadcast = (cxt: Vue, componentName: any, eventName: string, params: unknown) => {
  const self = cxt;
  self.$children.forEach((child: Vue) => {
    const name = child.$options.name || '';
    if (name === componentName) {
      child.$emit(eventName, params);
    } else {
      broadcast(child, componentName, eventName, params);
    }
  });
};
export default defineComponent({
  name: 'Dropdown',
  componentName: 'Dropdown',
  props: {
    trigger: {
      type: String,
      default: 'hover',
    },
    splitButton: Boolean,
    hideOnClick: {
      type: Boolean,
      default: true,
    },
    /**
     * 弹出框方向
     * 'down' | 'up'
     */
    direction: {
      type: String,
      default: 'down',
    },
    visibleArrow: {
      default: true,
    },
    showTimeout: {
      type: Number,
      default: 250,
    },
    hideTimeout: {
      type: Number,
      default: 150,
    },
    tabindex: {
      type: Number,
      default: 0,
    },
  },
  setup(props, context) {
    const currComponent: ComponentInternalInstance | null = getCurrentInstance();
    const dropdown: any = (currComponent as ComponentInternalInstance).proxy;
    const data = reactive({
      timeout: 0,
      visible: false,
      listId: `dropdown-menu-${generateId()}`,
    });
    const triggerElm: UnwrapRef<TriggerElm> = reactive({
      ele: null,
      disabled: false,
    });
    const dropdownElm: Ref<HTMLElement | null> = ref(null);
    const popperElm: Ref<HTMLElement | null> = ref(null);
    // eslint-disable-next-line no-undef
    const menuItems: Ref<NodeListOf<Element> | any> = ref([]);
    const menuItemsArray = ref([]);
    const methods = {
      initEvent() {
        const { trigger } = props;
        const { show, hide, handleClick } = this;
        const defaultSlot: any = context.slots.default;
        triggerElm.ele = defaultSlot()[0].elm as HTMLElement;
        if (trigger === 'hover') {
          triggerElm.ele.addEventListener('mouseenter', show);
          triggerElm.ele.addEventListener('mouseleave', hide);
          (dropdownElm.value as HTMLElement).addEventListener('mouseenter', show);
          (dropdownElm.value as HTMLElement).addEventListener('mouseleave', hide);
        } else if (trigger === 'click') {
          triggerElm.ele.addEventListener('click', handleClick);
        }
      },
      initAria() {
        (dropdownElm.value as HTMLElement).setAttribute('id', data.listId);
        (triggerElm.ele as HTMLElement).setAttribute('aria-haspopup', 'list');
        (triggerElm.ele as HTMLElement).setAttribute('aria-controls', data.listId);
        if (!props.splitButton) {
          // 自定义
          (triggerElm.ele as HTMLElement).setAttribute('role', 'button');
          (triggerElm.ele as HTMLElement).setAttribute('tabindex', `${props.tabindex}`);
          (triggerElm.ele as HTMLElement).setAttribute(
            'class',
            `${(triggerElm.ele as HTMLElement).getAttribute('class') || ''} w-dropdown-selfdefine`,
          ); // 控制
        }
      },

      initDomOperation() {
        dropdownElm.value = popperElm.value as HTMLElement;
        menuItems.value = dropdownElm.value.querySelectorAll("[tabindex='-1']");
        menuItemsArray.value = [].slice.call(menuItems.value);
        this.initEvent();
        this.initAria();
      },
      show() {
        if (triggerElm.disabled) return;
        window.clearTimeout(data.timeout);
        data.timeout = window.setTimeout(
          () => {
            data.visible = true;
          },
          props.trigger === 'click' ? 0 : props.showTimeout,
        );
      },
      hide() {
        if (triggerElm.disabled) return;
        this.removeTabindex();
        if (props.tabindex >= 0) {
          this.resetTabindex(triggerElm.ele as HTMLElement);
        }
        window.clearTimeout(data.timeout);
        data.timeout = window.setTimeout(
          () => {
            data.visible = false;
          },
          props.trigger === 'click' ? 0 : props.hideTimeout,
        );
      },
      resetTabindex(ele: HTMLElement) {
        // 下次tab时组件聚焦元素
        this.removeTabindex();
        ele.setAttribute('tabindex', '0'); // 下次期望的聚焦元素
      },

      removeTabindex() {
        (triggerElm.ele as HTMLElement).setAttribute('tabindex', '-1');
        menuItemsArray.value.forEach((item: HTMLElement) => {
          item.setAttribute('tabindex', '-1');
        });
      },
      triggerElmFocus() {
        (triggerElm.ele as HTMLElement).focus();
      },
      handleMenuItemClick(value: string) {
        if (props.hideOnClick) {
          data.visible = false;
        }
        context.emit('click', value);
      },
      handleClick() {
        if (triggerElm.disabled) return;
        if (data.visible) {
          this.hide();
        } else {
          this.show();
        }
      },
      onClickOutside() {
        if (props.trigger !== 'click') return;
        this.hide();
      },
    };
    onMounted(() => {
      dropdown.$on('menu-item-click', methods.handleMenuItemClick);
    });
    watch(
      () => data.visible,
      val => {
        broadcast(dropdown, 'DropdownMenu', 'visible', val);
        context.emit('visible-change', val);
      },
    );
    return {
      popperElm,
      dropdownElm,
      ...toRefs(data),
      ...methods,
    };
  },
});
